import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Home, Volume2, VolumeX, Sparkles, Star, Heart, Briefcase, Compass } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { MiniTutorial } from "@/components/MiniTutorial";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOROSCOPE_TUTORIAL_STEPS = [
  {
    emoji: "🌙",
    title: "Tu horóscopo diario personalizado",
    desc: "Cada día Cristopher canaliza un horóscopo único para ti, basado en tu signo y tu energía. Se renueva automáticamente cada 24 horas.",
  },
  {
    emoji: "🔊",
    title: "Escúchalo en voz alta",
    desc: "Toca el botón de la bocina para que tu horóscopo se lea en voz alta. Puedes pausarlo cuando quieras.",
  },
  {
    emoji: "🌐",
    title: "Cambia el idioma",
    desc: "Usa el selector de idioma para recibir tu horóscopo en Español, English, Français, Português o Italiano.",
  },
  {
    emoji: "⏳",
    title: "Tu acceso",
    desc: "El acceso al horóscopo te lo habilita el administrador (o se activa al liquidar un trabajo). Verás la fecha de vencimiento en la parte superior.",
  },
];

const LANGS = [
  { code: "es", label: "Español", voice: "es" },
  { code: "en", label: "English", voice: "en" },
  { code: "fr", label: "Français", voice: "fr" },
  { code: "pt", label: "Português", voice: "pt" },
  { code: "it", label: "Italiano", voice: "it" },
];

const cleanForSpeech = (s) =>
  String(s || "")
    .replace(/[\*_#`~\[\]\(\)\{\}<>$]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export default function HoroscopoPage() {
  const { user, authHeader } = useAuth();
  const navigate = useNavigate();
  const [access, setAccess] = useState(null);
  const [horoscope, setHoroscope] = useState(null);
  const [language, setLanguage] = useState("es");
  const [loading, setLoading] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const utterRef = useRef(null);
  const [stars] = useState(() => Array.from({ length: 30 }).map(() => ({
    top: `${Math.random() * 100}%`,
    left: `${Math.random() * 100}%`,
    fontSize: `${Math.random() * 10 + 8}px`,
    delay: `${Math.random() * 5}s`,
    duration: `${Math.random() * 4 + 3}s`,
  })));

  useEffect(() => {
    if (!user) {
      navigate("/ingresar");
      return;
    }
    (async () => {
      try {
        const ar = await axios.get(`${API}/users/horoscope/access`, { headers: authHeader() });
        setAccess(ar.data);
        if (ar.data?.has_access) {
          const hr = await axios.get(`${API}/users/horoscope/today?language=${language}`, { headers: authHeader() });
          setHoroscope(hr.data);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Error cargando horóscopo");
      } finally { setLoading(false); }
    })();
    return () => {
      try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
    };
    // eslint-disable-next-line
  }, [user, language]);

  const speak = () => {
    if (!horoscope?.text) return;
    try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
    const u = new SpeechSynthesisUtterance(cleanForSpeech(horoscope.text));
    u.lang = (LANGS.find((l) => l.code === language)?.voice || "es") + "-" + (language === "en" ? "US" : "ES");
    u.rate = 0.95;
    u.pitch = 1.0;
    u.onstart = () => setSpeaking(true);
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    utterRef.current = u;
    window.speechSynthesis.speak(u);
  };

  const stop = () => {
    try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
    setSpeaking(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" />
      </div>
    );
  }

  if (!access?.has_access) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 lg:px-12" data-testid="horoscope-locked">
        <div className="max-w-2xl mx-auto glass-dark rounded-2xl p-8 text-center space-y-4">
          <div className="text-6xl">🔒</div>
          <h1 className="font-serif text-3xl text-white">Horóscopos Diarios</h1>
          <p className="text-neutral-300 text-sm leading-relaxed">
            Tu acceso a los horóscopos diarios se desbloquea automáticamente cuando liquidas una lectura
            (30 días) o un trabajo de brujería (365 días).
          </p>
          <p className="text-amber-200 text-xs">
            {access?.reason === "expired"
              ? "Tu acceso expiró. Apartar una nueva lectura o trabajo para reactivarlo."
              : access?.reason === "disabled"
              ? "El administrador pausó tu acceso. Contáctalo para más información."
              : "Aún no tienes pagos liquidados que activen este beneficio."}
          </p>
          <div className="flex justify-center gap-2 pt-2">
            <Link to="/mi-cuenta?tab=pays" data-testid="horoscope-go-pays" className="btn-outline-gold px-4 py-2 rounded-full text-xs uppercase tracking-widest">Ver mis pagos</Link>
            <Link to="/" data-testid="horoscope-back-home" className="btn-gold px-4 py-2 rounded-full text-xs uppercase tracking-widest flex items-center gap-2"><Home className="w-3.5 h-3.5" /> Inicio</Link>
          </div>
        </div>
      </div>
    );
  }

  const sections = (horoscope?.text || "").split(/\n+/).filter(Boolean);
  const exp = access.expires_at ? new Date(access.expires_at).toLocaleDateString() : null;

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 lg:px-12 relative overflow-hidden" data-testid="horoscope-page">
      <MiniTutorial storageKey="ca_tut_horoscopo" title="Horóscopo Diario" steps={HOROSCOPE_TUTORIAL_STEPS} />
      {/* Floating stars background */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        {stars.map((s, i) => (
          <div
            key={i}
            className="absolute text-[#d4af37]/30 animate-pulse"
            style={{ top: s.top, left: s.left, fontSize: s.fontSize, animationDelay: s.delay, animationDuration: s.duration }}
          >✦</div>
        ))}
      </div>

      <div className="max-w-3xl mx-auto relative z-10">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <div>
            <h1 className="font-serif text-3xl lg:text-4xl text-white flex items-center gap-2">
              <span className="text-4xl animate-pulse">{horoscope?.icon || "🌙"}</span>
              Tu Horóscopo de Hoy
            </h1>
            <p className="text-xs text-neutral-400 mt-1">
              {new Date().toLocaleDateString("es-MX", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
              {exp && <span className="text-[#d4af37] ml-2">· acceso hasta {exp}</span>}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white text-xs w-32" data-testid="horoscope-lang"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-[#d4af37]/30 text-white">
                {LANGS.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {speaking ? (
              <button data-testid="horoscope-stop" onClick={stop} className="btn-outline-gold px-3 py-2 rounded-full text-xs uppercase tracking-widest flex items-center gap-2"><VolumeX className="w-3.5 h-3.5" /> Detener</button>
            ) : (
              <button data-testid="horoscope-speak" onClick={speak} className="btn-gold px-3 py-2 rounded-full text-xs uppercase tracking-widest flex items-center gap-2"><Volume2 className="w-3.5 h-3.5" /> Escuchar</button>
            )}
            <Link to="/" data-testid="horoscope-home" className="btn-outline-gold px-3 py-2 rounded-full text-xs uppercase tracking-widest flex items-center gap-2"><Home className="w-3.5 h-3.5" /></Link>
          </div>
        </div>

        <div className="glass-dark rounded-3xl p-6 lg:p-10 border-2 border-[#d4af37]/30 shadow-2xl shadow-purple-500/10">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30">
              <Sparkles className="w-3.5 h-3.5 text-[#d4af37]" />
              <span className="text-[10px] uppercase tracking-widest text-[#d4af37]">Tema: {horoscope?.theme || "el día"}</span>
            </div>
          </div>
          <div className="prose prose-invert max-w-none space-y-4">
            {sections.map((p, i) => {
              const icons = [Star, Heart, Briefcase, Compass];
              const Icon = icons[i % icons.length];
              return (
                <p
                  key={i}
                  data-testid={`horoscope-paragraph-${i}`}
                  className="text-neutral-100 text-base leading-relaxed flex gap-3 items-start"
                  style={{ animation: `fadeIn 0.6s ease-out ${i * 0.15}s both` }}
                >
                  <Icon className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" />
                  <span>{p}</span>
                </p>
              );
            })}
          </div>
          <div className="text-center mt-6 pt-4 border-t border-white/10">
            <p className="text-[11px] text-neutral-500 italic">~ Generado especialmente para {user?.name || user?.username} por CrisAngel Astral ~</p>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
